import { useDeferredValue, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/admin/ui";
import { OrdersTable } from "@/components/admin/OrdersTable";
import { OrderDialog } from "@/components/admin/OrderDialog";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { EmptyState, PageLoader } from "@/components/common";
import { api, fetcher, formatApiError, invalidateAdmin } from "@/lib/api";
import { ORDER_STATUS } from "@/lib/format";

export default function Orders() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const q = useDeferredValue(search.trim());
  const [dialog, setDialog] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["admin-orders", status, q],
    queryFn: () => fetcher("/admin/orders", { status: status === "all" ? undefined : status, q: q || undefined }),
  });
  const refresh = () => invalidateAdmin(qc);

  const updateStatus = useMutation({
    mutationFn: ({ id, value }) => api.put(`/admin/orders/${id}`, { status: value }),
    onSuccess: () => { toast.success("Status diperbarui"); refresh(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/admin/orders/${id}`),
    onSuccess: () => { toast.success("Pesanan dihapus"); setToDelete(null); refresh(); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <PageHeader title="Pesanan" subtitle="Semua pesanan dari website dan input manual">
        <Button onClick={() => setDialog({})} data-testid="admin-order-btn-add"><Plus /> Tambah pesanan</Button>
      </PageHeader>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari kode, nama, no. WA, trip" className="pl-9" data-testid="admin-order-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-56" data-testid="admin-order-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="admin-order-filter-status-option-all">Semua status</SelectItem>
            {Object.entries(ORDER_STATUS).map(([k, m]) => <SelectItem key={k} value={k} data-testid={`admin-order-filter-status-option-${k}`}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {isLoading && <PageLoader className="min-h-[30vh]" />}
      {!isLoading && orders.length === 0 && <EmptyState icon={ClipboardList} title="Belum ada pesanan" text="Pesanan dari website akan muncul di sini." testId="admin-orders-empty" />}
      {orders.length > 0 && (
        <OrdersTable
          orders={orders}
          onEdit={(o) => setDialog({ order: o })}
          onDelete={setToDelete}
          onStatus={(o, value) => updateStatus.mutate({ id: o.id, value })}
        />
      )}
      {dialog && <OrderDialog order={dialog.order} onClose={() => setDialog(null)} onSaved={refresh} />}
      <ConfirmDelete
        open={!!toDelete}
        title="Hapus pesanan?"
        description={`Pesanan ${toDelete?.code} dan pemasukan otomatisnya akan dihapus permanen.`}
        onConfirm={() => remove.mutate(toDelete.id)}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}
